import { html, css } from 'lit';
import KaiElement from '../base';

const types = {
  LARGE: 'large',
  NORMAL: 'normal',
  SMALL: 'small',
};

class HeaderElement extends KaiElement {
  static get is() {
    return 'kai-header';
  }

  static get properties() {
    return {
      type: { type: String },
      primarytitle: { type: String },
      subtitle: { type: String },
      menuoptions: { type: Array },
    };
  }

  static get styles() {
    return [
      super.styles,
      css`
        .header {
          width: 100%;
          height: 8rem;
          display: flex;
          align-items: center;
          box-sizing: border-box;
          color: var(--color-gs100);
          background-color: var(--header-background-color, var(--color-gs00));
        }

        .content {
          flex-grow: 1;
          margin: 0 2rem;
          overflow: hidden;
          text-align: left;
        }

        .content:-moz-dir(rtl) {
          text-align: right;
        }

        .title,
        .sub-title {
          overflow: hidden;
          white-space: nowrap;
          text-overflow: ellipsis;
        }

        .fixed-text {
          font-size: 1.6rem;
        }

        .menu,
        .header ::slotted([slot='header-left-slot']),
        .header ::slotted([slot='header-right-slot']) {
          font-size: 3.2rem;
        }

        .menu,
        .header ::slotted([slot='header-right-slot']) {
          margin-right: 1.5rem;
          margin-left: unset;
        }

        .menu:-moz-dir(rtl),
        .header ::slotted([slot='header-right-slot']):-moz-dir(rtl) {
          margin-left: 1.5rem;
          margin-right: unset;
        }

        .header ::slotted([slot='header-left-slot']) {
          margin-left: 1.5rem;
          margin-right: -1rem;
        }

        .header ::slotted([slot='header-left-slot']):-moz-dir(rtl) {
          margin-left: -1rem;
          margin-right: 1.5rem;
        }

        .menu:focus,
        .menu.open,
        .header ::slotted([slot='header-left-slot']:focus),
        .header ::slotted([slot='header-right-slot']:focus) {
          color: var(--theme-color);
        }

        .menu:active,
        .header ::slotted([slot='header-left-slot']:active),
        .header ::slotted([slot='header-right-slot']:active) {
          color: var(--color-gs60);
        }

        .header ::slotted(kai-textbutton) {
          --baseline: var(--root-font-size) !important;
        }

        #menuicon {
          z-index: var(--z-index-optionmenu-icon);
        }
      `,
    ];
  }

  constructor() {
    super();
    this.type = types.NORMAL;
    this.primarytitle = '';
    this.subtitle = '';
    this.menuoptions = [];
    this._observerList = [];
  }

  get header() {
    return this.shadowRoot.querySelector('.header');
  }

  get optionmenu() {
    return this.shadowRoot.querySelector('kai-optionmenu');
  }

  get menuicon() {
    return this.shadowRoot.querySelector('#menuicon');
  }

  _onMenuClick(evt) {
    evt.stopPropagation();
    const from = this.header.offsetTop - this.header.offsetParent.scrollTop;
    const to =
      this.header.offsetHeight +
      this.header.offsetTop -
      this.header.offsetParent.scrollTop;
    this.optionmenu.position = { from, to };
    this.optionmenu.open = !this.optionmenu.open;
  }

  _slotMarginChange() {
    const rightSlot = this.shadowRoot.querySelector(
      'slot[name=header-right-slot]'
    );
    if (rightSlot) {
      const nodes = rightSlot.assignedNodes();
      nodes.forEach((node, index) => {
        if (this.isRtl) {
          if (node.localName === 'kai-textbutton') {
            /* kai-textbutton is slotted, change the margin */
            node.style.marginLeft = '1rem';
            node.style.marginRight = 'unset';
            if (index === 0) {
              node.style.marginRight = '-1rem';
            }
          } else if (index === 0) {
            node.style.marginLeft = '1.5rem';
            node.style.marginRight = '-0.5rem';
          }
        } else {
          if (node.localName === 'kai-textbutton') {
            /* kai-textbutton is slotted, change the margin */
            node.style.marginRight = '1rem';
            node.style.marginLeft = 'unset';
            if (index === 0) {
              node.style.marginLeft = '-1rem';
            }
          } else if (index === 0) {
            node.style.marginLeft = '-0.5rem';
            node.style.marginRight = '1.5rem';
          }
        }
      });
    }
  }

  updated(changedProperties) {
    this._slotMarginChange();

    if (changedProperties.has('menuoptions')) {
      this._disconnectObservers();
      this._observerList = [{
        name: 'optionmenu',
        observer: this.optionmenu,
      }].map(({ name, observer }) => {
        return observer && new MutationObserver((mutationsList) => {
          const detail = {
            [name]: {},
          };
          for (let mutation of mutationsList) {
            if (mutation.type === 'attributes') {
              detail[name][mutation.attributeName] =
                mutation.target[mutation.attributeName];
            }
          }
          if (Object.keys(detail[name]).length) {
            const customEvent = new CustomEvent('mutated', {
              detail,
              bubbles: true,
              composed: true,
            });
            this.dispatchEvent(customEvent);
          }
        }).observe(observer, {
          attributes: true,
        });
      });
    }
  }

  disconnectedCallback() {
    this._disconnectObservers();
  }

  _disconnectObservers() {
    this._observerList.forEach(observer => {
      observer && observer.disconnect();
    });
    this._observerList = [];
  }

  _getPrimaryTextStyle() {
    switch (this.type) {
      case types.LARGE:
        return 'h1';
      case types.NORMAL:
        return 'h2';
      case types.SMALL:
        return 'h3';
      default:
        return 'h2';
    }
  }

  _optionmenuChange(e) {
    const isOpen = e.detail.open;
    if (isOpen) {
      this.menuicon.classList.add('open');
    } else {
      this.menuicon.classList.remove('open');
    }
  }

  render() {
    return html`
      <div class="header">
        <slot name="header-left-slot"> </slot>
        <div class="content" role="heading" aria-level="1">
          <div class="title ${this._getPrimaryTextStyle()}" dir="auto">
            ${this.primarytitle}
          </div>
          <div
            class="sub-title body-2 fixed-text"
            ?hidden="${!this.subtitle}"
            dir="auto"
          >
            ${this.subtitle}
          </div>
        </div>
        <slot name="header-right-slot"></slot>
        ${this.menuoptions && this.menuoptions.length > 0
          ? html`
              <i
                id="menuicon"
                class="menu"
                data-icon="menu"
                @click=${this._onMenuClick}
              ></i>
              <kai-optionmenu
                .options=${this.menuoptions}
                @visibilitychange=${this._optionmenuChange}
              ></kai-optionmenu>
            `
          : ''}
      </div>
    `;
  }
}

customElements.define(HeaderElement.is, HeaderElement);
